import React, { useMemo, Component } from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingDown, ArrowRight } from 'lucide-react';
import { ModuleData, ModuleId, AppState } from '../../types';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { computeSkillMasteries } from '../../utils/skillAggregation';
import { formatSkill } from '../../utils/formatters';
import { GroupId } from '../../config/systemConfig';
import * as Icons from 'lucide-react';
interface FocusAreasProps {
  state: AppState;
  modules: ModuleData[];
  selectedGroup: GroupId;
  onStartPractice: (moduleId: ModuleId) => void;
}
export function FocusAreas({
  state,
  modules,
  selectedGroup,
  onStartPractice
}: FocusAreasProps) {
  // Find weak skills across ALL modules
  const weakSkills = useMemo(() => {
    const allSkills: Array<{
      moduleId: ModuleId;
      skillName: string;
      masteryScore: number;
      trend: 'improving' | 'stable' | 'struggling';
      moduleTitle: string;
      moduleIcon: string;
      moduleColor: string;
    }> = [];
    modules.forEach(module => {
      const skillMasteries = computeSkillMasteries(module.id, selectedGroup, state.questionStates);
      skillMasteries.forEach(skill => {
        // Include if struggling OR low mastery (< 50%)
        if (skill.trend === 'struggling' || skill.masteryScore < 50) {
          allSkills.push({
            moduleId: module.id,
            skillName: skill.skill,
            masteryScore: skill.masteryScore,
            trend: skill.trend,
            moduleTitle: module.title,
            moduleIcon: module.icon,
            moduleColor: module.color
          });
        }
      });
    });
    // Sort by score (lowest first) and take top 3
    return allSkills.sort((a, b) => a.masteryScore - b.masteryScore).slice(0, 3);
  }, [state.questionStates, modules, selectedGroup]);
  if (weakSkills.length === 0) return null;
  return <div className="h-full" data-id="element-312">
      <div className="flex items-center gap-2 mb-4" data-id="element-313">
        <Target className="w-5 h-5 text-amber-600" data-id="element-314" />
        <h2 className="text-lg font-bold text-slate-900" data-id="element-315">Aandachtspunten</h2>
      </div>

      <div className="space-y-3" data-id="element-316">
        {weakSkills.map((skill, index) => {
        const IconComponent = (Icons as any)[skill.moduleIcon] || Icons.HelpCircle;
        return <motion.div key={`${skill.moduleId}-${skill.skillName}`} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} data-id="element-317">
              <Card className="p-4 border-amber-100 bg-amber-50/30 hover:bg-amber-50/60 transition-colors flex items-center justify-between gap-4" data-id="element-318">
                <div className="flex items-center gap-4 min-w-0" data-id="element-319">
                  <div className="p-2.5 bg-white rounded-xl shadow-sm border border-amber-100 flex-shrink-0" data-id="element-320">
                    <IconComponent className={`w-5 h-5 ${skill.moduleColor}`} data-id="element-321" />
                  </div>

                  <div className="min-w-0" data-id="element-322">
                    <h3 className="font-bold text-slate-900 capitalize truncate" data-id="element-323">
                      {formatSkill(skill.skillName)}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-324">
                      <span data-id="element-325">{skill.moduleTitle}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-326" />
                      <span className="font-bold text-amber-600 flex items-center gap-1" data-id="element-327">
                        {skill.masteryScore}%
                        {skill.trend === 'struggling' && <TrendingDown className="w-3 h-3" data-id="element-328" />}
                      </span>
                    </div>
                  </div>
                </div>

                <Button onClick={() => onStartPractice(skill.moduleId)} variant="secondary" size="sm" className="bg-white border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300 flex-shrink-0" data-id="element-329">
                  Oefenen
                </Button>
              </Card>
            </motion.div>;
      })}
      </div>
    </div>;
}